import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../apiConfig'; 
import io from 'socket.io-client';
import OrderCard from './OrderCard';
import OrderStats from './OrderStats';
import { styles } from './AdminOrders.styles'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import OrderActionModal from '../OrderActionModal'; 

// Բոլոր հաշվիչների ներմուծումը
import DemolitionCalculator from '../calculators/Demolition'; 
import BnHashvich from '../calculators/MovingCalculator';
import UnifiedLogisticsERP from '../calculators/UnifiedLogisticsERP'; 
import FuraCalculator from '../calculators/FuraCalculator';
import CleaningCalculator from '../calculators/CleaningCalculator'; 
import MasterCalculator from '../calculators/MasterCalculator'; 
import MachineryCalculator from '../calculators/MachineryCalculator';

const socket = io(API_BASE, {
    transports: ['websocket', 'polling'],
    auth: { token: localStorage.getItem('token') }
});

// 🔥 ԱՅՍ ՄՈԴԱԼԸ ՀԱՅՏՆՎՈՒՄ Է ԳԻՆԸ ՓՈԽԵԼՈՒ ԺԱՄԱՆԱԿ
const OrderModals = ({ step, setStep, selectedOrder, handleRecalculationSubmit }) => {
    const { t } = useTranslation();
    const [localResult, setLocalResult] = useState(null);

    useEffect(() => {
        setLocalResult(null);
    }, [selectedOrder, step]);

    if (step !== 'edit_price' || !selectedOrder) return null;

    // Ֆունկցիա, որը կաշխատի, երբ հաշվիչը տվյալ ուղարկի
    const onDataReceived = (dataOrPrice, summary, fullData) => {
        console.log("Admin received data:", dataOrPrice, summary, fullData);
        
        // Քանի որ տարբեր հաշվիչներ տարբեր ձև են ուղարկում, ստուգում ենք բոլոր տարբերակները
        if (typeof dataOrPrice === 'object' && dataOrPrice !== null) {
            setLocalResult({
                ...dataOrPrice,
                price: dataOrPrice.price || dataOrPrice.total_price || dataOrPrice.total
            });
        } else {
            setLocalResult({
                price: dataOrPrice,
                details: summary,
                ...fullData
            });
        }
    };

    const renderCalculator = () => {
        const sId = selectedOrder.service_id ? parseInt(selectedOrder.service_id) : null;
        const typeName = (selectedOrder.service_type || selectedOrder.service_name || "").toLowerCase().trim();

        const commonProps = { 
            order: selectedOrder, 
            setStep, 
            // Տարբեր հաշվիչներ տարբեր props են ուզում տվյալ հետ ուղարկելու համար
            onSubmit: onDataReceived,
            setCalcData: onDataReceived,
            onCalculate: onDataReceived,
            onResultChange: onDataReceived 
        };

        if (sId === 1 || typeName.includes("քանդման")) return <DemolitionCalculator {...commonProps} />;
        if (sId === 2 || typeName.includes("աղբի")) return <UnifiedLogisticsERP {...commonProps} />;
        if (sId === 3 || typeName.includes("բնակարան") || typeName.includes("տեղափոխում")) return <BnHashvich {...commonProps} />;
        if (sId === 4 || typeName.includes("ֆուրա")) return <FuraCalculator {...commonProps} />;
        if (sId === 5 || typeName.includes("մաքրում")) return <CleaningCalculator {...commonProps} />;
        if (sId === 6 || typeName.includes("վարպետ")) return <MasterCalculator {...commonProps} />;
        if (sId === 7 || typeName.includes("տեխնիկա")) return <MachineryCalculator {...commonProps} />;

        return <div style={{padding: '20px'}}>{t('calculator_not_available')}</div>;
    };

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <button style={styles.closeBtn} onClick={() => { setStep(0); setLocalResult(null); }}>×</button>
                <div style={{ padding: '10px' }}>
                    {renderCalculator()}

                    {localResult && localResult.price && (
                        <div style={{
                            marginTop: '20px', padding: '15px', background: '#eef2ff',
                            borderRadius: '15px', border: '2px solid #4318ff', textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#1b2559', marginBottom: '10px' }}>
                                Գինը: {Number(localResult.price).toLocaleString()} ֏
                            </div>
                            <button 
                                onClick={() => handleRecalculationSubmit(localResult)}
                                style={{
                                    width: '100%', padding: '14px', borderRadius: '12px',
                                    backgroundColor: '#4318ff', color: '#fff', border: 'none',
                                    fontWeight: 'bold', cursor: 'pointer'
                                }}
                            >
                                🚀 ՀԱՍՏԱՏԵԼ ԵՎ ՈՒՂԱՐԿԵԼ
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AdminOrders = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [visibleCount, setVisibleCount] = useState(10); 
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [step, setStep] = useState(0);

    const getHeaders = useCallback(() => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }), []);

    const fetchOrders = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/orders/admin/all`, { headers: getHeaders() });
            const data = await res.json();
            
            const processedData = (Array.isArray(data) ? data : []).map(order => ({
                ...order,
                workers: Array.isArray(order.workers) ? order.workers : []
            }));
            setOrders(processedData);
        } catch (e) { 
            console.error("Fetch orders error:", e); 
        } finally { 
            if (showLoading) setLoading(false); 
        }
    }, [getHeaders]);

    useEffect(() => {
        fetchOrders(); 
        socket.on('order_status_changed', (data) => {
            setOrders(prevOrders => prevOrders.map(order => {
                if (Number(order.id) === Number(data.orderId)) {
                    return { 
                        ...order, 
                        status: data.status,
                        accepted_count: data.accepted_count,
                        accepted_drivers_count: data.accepted_drivers_count,
                        workers: data.workers || order.workers
                    };
                }
                return order;
            }));
        });
        socket.on('admin_order_update', () => fetchOrders(false));
        socket.on('newOrderNotification', () => fetchOrders(false));
        socket.on('worker_assigned', () => fetchOrders(false));

        return () => {
            socket.off('order_status_changed');
            socket.off('admin_order_update');
            socket.off('newOrderNotification');
            socket.off('worker_assigned');
        };
    }, [fetchOrders]);

  const updateStatus = async (id, newStatus, note = "") => {
    try {
        let res;

        // 🟢 Եթե Ադմինը սեղմում է "Ավարտել" (in_progress -> pending_finish)
        if (newStatus === 'pending_finish') {
            res = await fetch(`${API_BASE}/api/orders/finish`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ 
                    orderId: id, 
                    workerId: null // Ադմինի դեպքում workerId-ն պարտադիր չէ
                })
            });
        } else {
            // 🔴 Մնացած բոլոր ստատուսների համար (cancel, confirm և այլն)
            res = await fetch(`${API_BASE}/api/orders/status/${id}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ status: newStatus, cancel_reason: note })
            });
        }

        if (res.ok) {
            // Եթե Socket-ը Frontend-ում կա, ստիպում ենք թարմանալ (թեև սերվերն էլ է emit անում)
            fetchOrders(false);
        } else {
            const errorText = await res.text();
            console.error("Server responded with error:", errorText);
        }
    } catch (err) {
        console.error("🔥 Error updating status:", err);
    }
};
    const removeWorker = async (orderId, workerId) => {
        if(!window.confirm(t('confirm_remove_worker'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/orders/remove-worker`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ orderId, workerId })
            });
            if (res.ok) fetchOrders(false);
        } catch (e) { console.error(e); }
    };

    const confirmOrderByAdmin = async (orderId) => {
        try {
            const res = await fetch(`${API_BASE}/api/orders/admin-confirm`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ orderId })
            });
            if (res.ok) fetchOrders(false);
        } catch (err) { console.error(err); }
    };

    // ✅ ԱՅՍՏԵՂ ԿԱՏԱՐՎՈՒՄ Է ՃԻՇՏ ՈՒՂԱՐԿՈՒՄԸ ՍԵՐՎԵՐԻՆ
    const handleRecalculationSubmit = async (finalCalc) => {
        if (!selectedOrder) return;
        try {
            const payload = {
                parent_id: Number(selectedOrder.id),
                user_id: Number(selectedOrder.user_id),
                service_id: selectedOrder.service_id,
                service_name: selectedOrder.service_name || "Ծառայություն",
                category_name: selectedOrder.category_name || "LOGISTICS",
                customer_name: selectedOrder.customer_name || "Հաճախորդ",
                customer_phone: selectedOrder.customer_phone || "",
                location: selectedOrder.location || "N/A",
                total_price: Number(finalCalc.price), 
                truck_price: Number(finalCalc.truck_price || 0),
                other_expenses: Number(finalCalc.other_expenses || 0),
                message: `${t('price_change_prefix')}: ${finalCalc.details || finalCalc.message || ""}`,
                order_date: selectedOrder.order_date,
                order_time: selectedOrder.order_time,
                status: 'new' // Նոր պատվեր ենք սարքում հաճախորդի համար
            };

            const res = await fetch(`${API_BASE}/api/orders`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setStep(0);
                setSelectedOrder(null);
                fetchOrders(false);
                alert(t('recalculation_sent_success'));
            } else {
                const errorData = await res.text();
                console.error("Server Error:", errorData);
                alert("Սխալ սերվերի վրա (500): Ստուգիր console-ը");
            }
        } catch (err) { console.error("Request Error:", err); }
    };

    const stats = useMemo(() => {
        const getCount = (s) => orders.filter(o => o.status?.toLowerCase().trim() === s.toLowerCase()).length;
        return {
            total: orders.length,
            new: getCount('new'),
            processing: getCount('processing'),
            pending_admin: getCount('pending_admin'),
            confirmed: getCount('confirmed'),
            mismatch: getCount('mismatch'),
            modified: getCount('modified'),
            in_progress: getCount('in_progress'),
            pending_finish: getCount('pending_finish'),
            finished: getCount('finished'),
            cancelled: getCount('cancelled'),
        };
    }, [orders]);

    const filteredOrders = useMemo(() => {
        return orders.filter(o => filter === 'all' || o.status?.toLowerCase().trim() === filter.toLowerCase());
    }, [orders, filter]);

    const handleAdminAction = async (actionType, payload) => {
        if (actionType === 'pay_finish') {
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                const res = await fetch(`${API_BASE}/api/orders/confirm-finish`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    },
                    body: JSON.stringify({
                        orderId: payload.id,
                        rating: payload.rating,
                        paymentMethod: payload.paymentMethod
                    })
                });
                const data = await res.json();
                if (data.success) {
                    setStep(0);
                    fetchOrders(false);
                    alert(t('order_closed_success'));
                }
            } catch (err) { console.error(err); }
        } else {
            setStep(0);
        }
    };

    return (
        <div style={styles.dashboard}>
            <OrderStats stats={stats} />

            <div style={styles.mainHeader}>
                <h1 style={styles.title}>{t('admin_orders_title')}</h1>
                <button onClick={() => fetchOrders(true)} style={styles.refreshBtn}>
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                </button>
            </div>

            <div style={styles.filterGroup}>
                {[
                    { id: 'all', label: t('filter_all') },
                    { id: 'new', label: t('filter_new') },
                    { id: 'processing', label: t('filter_processing') },
                    { id: 'pending_admin', label: t('filter_pending') },
                    { id: 'confirmed', label: t('filter_confirmed') },
                    { id: 'mismatch', label: t('filter_problem') },
                    { id: 'modified', label: t('filter_modified') },
                    { id: 'in_progress', label: t('filter_in_progress') },
                    { id: 'pending_finish', label: t('filter_waiting_finish') }, 
                    { id: 'finished', label: t('filter_finished') },
                    { id: 'cancelled', label: t('filter_cancelled') }
                ].map(f => (
                    <button 
                        key={f.id} 
                        onClick={() => { setFilter(f.id); setVisibleCount(10); }} 
                        style={styles.filterTab(filter === f.id)}
                    >
                        {f.label}
                        {stats[f.id] > 0 && (
                            <span style={styles.badge(filter === f.id)}>
                                {stats[f.id]}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            <div style={styles.verticalList}>
                {filteredOrders.slice(0, visibleCount).map(o => (
                    <OrderCard 
                        key={o.id} 
                        order={o} 
                        onUpdate={updateStatus} 
                        onRemoveWorker={removeWorker} 
                        onAdminConfirm={confirmOrderByAdmin}
                        onOpenRevision={(ord) => {
                            setSelectedOrder(ord);
                            setStep('edit_price');
                        }}
                        onOpenFinish={(ord) => {
                            setSelectedOrder(ord);
                            setStep('finish_confirm');
                        }}
                    />
                ))}
            </div>

            {visibleCount < filteredOrders.length && (
                <button onClick={() => setVisibleCount(prev => prev + 10)} style={styles.loadMore}>
                    {t('btn_see_next_10')}
                </button>
            )}

            <OrderModals 
                step={step}
                setStep={setStep}
                selectedOrder={selectedOrder}
                handleRecalculationSubmit={handleRecalculationSubmit}
            />

            <OrderActionModal 
                isOpen={step === 'finish_confirm'} 
                data={selectedOrder ? {
                    ...selectedOrder,
                    type: 'ORDER_FINISHED_APPROVE',
                    totalPrice: selectedOrder.total_price,
                    service: selectedOrder.service_type || selectedOrder.service_name,
                    can_pay_later: 1 
                } : null}
                onAction={handleAdminAction}
                socket={null} 
            />
        </div>
    );
};

export default AdminOrders;